import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { formatCurrency } from "@/i18n/format";
import type { Contract, ModeratorsContractsListStatus } from "@/services/moderators/generated/model";
import { useModeratorsContractsList } from "@/services/moderators/generated/moderators-deals/moderators-deals";
import "./ModeratorPage.css";
import "./ModeratorContractsPage.css";

const STATUS_OPTIONS: ReadonlyArray<"" | ModeratorsContractsListStatus> = [
  "",
  "draft",
  "active",
  "completed",
  "cancelled",
];

const ModeratorContractsPage = () => (
  <ModeratorLayout>
    <ContractsContent />
  </ModeratorLayout>
);

const ContractsContent = () => {
  const { t } = useTranslation(["deals", "common"]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | ModeratorsContractsListStatus>("");

  const queryParams = useMemo(() => {
    const params: { search?: string; status?: ModeratorsContractsListStatus } = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter) params.status = statusFilter;
    return params;
  }, [search, statusFilter]);

  const { data, isLoading, error } = useModeratorsContractsList(queryParams);
  const contracts = unwrapPaginated<Contract>(data);

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("contracts.title")}</h1>
          <p className="mod-page__subtitle">{t("contracts.subtitle")}</p>
        </div>
        {/* Contracts are 1:1 with deals (MatchResult), so authoring goes through
            the deal detail page where the deal is the natural anchor. */}
        <Link to="/moderator/deals" className="mod-page__btn mod-page__btn--primary">
          <i className="fa-solid fa-plus" />
          <span style={{ marginLeft: 6 }}>{t("contracts.createFromDeal")}</span>
        </Link>
      </header>

      <form
        className="mod-page__toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchInput);
        }}
      >
        <input
          type="search"
          className="mod-page__input"
          placeholder={t("contracts.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          className="mod-page__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | ModeratorsContractsListStatus)}
        >
          <option value="">{t("contracts.allStatuses")}</option>
          {STATUS_OPTIONS.filter((s): s is ModeratorsContractsListStatus => s !== "").map((s) => (
            <option key={s} value={s}>
              {t(`shared.contractStatus.${s}`)}
            </option>
          ))}
        </select>
        <button type="submit" className="mod-page__btn mod-page__btn--primary">
          {t("common:actions.apply")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          onClick={() => {
            setSearchInput("");
            setSearch("");
            setStatusFilter("");
          }}
        >
          {t("shared.reset")}
        </button>
      </form>

      {isLoading ? <div className="mod-page__loading">{t("contracts.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("contracts.loadError")}</div> : null}
      {!isLoading && contracts.length === 0 ? <div className="mod-page__empty">{t("contracts.empty")}</div> : null}

      {contracts.map((contract) => (
        <article key={contract.id} className="mod-page__card mod-contracts__card">
          <div className="mod-page__card-body">
            <h2 className="mod-page__card-title">
              {t("contracts.cardTitle", { id: contract.id })}
              {contract.referenceId ? ` · ${contract.referenceId}` : ""}
            </h2>
            <p className="mod-page__card-meta">
              {contract.companyAName || "—"} ↔ {contract.companyBName || "—"}
            </p>
            <p className="mod-page__card-meta">
              <span className={`mod-page__chip ${statusChipClass(contract.status)}`}>{contract.status}</span>
              <span className="mod-page__chip">{contract.contractType}</span>
              {contract.contractValue ? (
                <span className="mod-page__chip">{formatCurrency(contract.contractValue)}</span>
              ) : null}
            </p>
            {contract.startDate || contract.endDate ? (
              <p className="mod-page__card-meta">
                {contract.startDate ?? "—"} → {contract.endDate ?? "—"}
              </p>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
};

function statusChipClass(status: string): string {
  if (status === "active") return "mod-page__chip--success";
  if (status === "completed") return "mod-page__chip--success";
  if (status === "cancelled") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

export default ModeratorContractsPage;
